import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';

import clsx from 'clsx';
import {
  getEnganche,
  getEngancheRecursosPropios,
  getImporte,
  getMontoCreditoInfonavit,
  getMontoGastosEscritura,
  getPercentageEnganche,
  getPlazo,
  getPropietyValue,
  getSaldoSubcuentaInfonavit,
} from '../../../../../utils/credit';
import { convertirAMoneda } from '../../../../../utils/currency';

import useStyles from './useStyles';
import { selectPerfiladorGastosEscrituraPercentage } from '../../../../../store/perfilador';

export const ShowConditionsContainer = React.memo(({
  title,
  showPropietyValue,
  showEnganche,
  showImporte,
  showPlazo,
  showGastosEscritura,
  includeCents,
  children,
}) => {
  const classes = useStyles();
  const gastosEscrituraPercentage = useSelector(selectPerfiladorGastosEscrituraPercentage);
  const montoSubcuentaInfonavit = getSaldoSubcuentaInfonavit();

  const showMontoSubcuentaInfonavit = useMemo(
    () => montoSubcuentaInfonavit > 0,
    [montoSubcuentaInfonavit],
  ) || false;

  const conditions = useMemo(() => {
    const plazoNumber = getPlazo();
    const enganchePercentage = getPercentageEnganche();
    const plazoValue = typeof plazoNumber === 'number' ? `${plazoNumber} años` : plazoNumber;

    return [
      {
        id: 'propiety-value',
        label: 'Valor de propiedad',
        value: convertirAMoneda(getPropietyValue(), includeCents),
        show: showPropietyValue,
        classNames: {
          boldLabel: true,
          boldValue: true,
        },
      },
      {
        id: 'importe',
        label: 'Monto del crédito',
        value: convertirAMoneda(getImporte(), includeCents),
        show: showImporte,
        classNames: {
          boldLabel: true,
          boldValue: true,
        },
      },
      {
        id: 'engancheTitle',
        label: 'Enganche',
        show: showMontoSubcuentaInfonavit,
        value: '',
        classNames: {
          boldLabel: true,
        },
      },
      {
        id: 'engancheRecursosPropios',
        label: 'Recursos propios',
        value: convertirAMoneda(getEngancheRecursosPropios(), includeCents),
        show: showMontoSubcuentaInfonavit,
        classNames: {
          leftIndentation: true,
        },
      },
      {
        id: 'montoSubcuentaInfonavit',
        label: 'Subcuenta Infonavit',
        value: convertirAMoneda(getSaldoSubcuentaInfonavit(), includeCents),
        show: showMontoSubcuentaInfonavit,
        classNames: {
          leftIndentation: true,
        },
      },
      {
        id: 'montoCreditoInfonavit',
        label: 'Crédito Infonavit',
        value: convertirAMoneda(getMontoCreditoInfonavit(), includeCents),
        show: showMontoSubcuentaInfonavit,
        classNames: {
          leftIndentation: true,
        },
      },
      {
        id: 'enganche',
        label: `Enganche (${enganchePercentage}%)`,
        value: convertirAMoneda(getEnganche() || 0, includeCents),
        show: !showMontoSubcuentaInfonavit,
        classNames: {
          boldValue: true,
          boldLabel: true,
        },
      },
      {
        id: 'enganche',
        label: `Enganche Total (${enganchePercentage}%)`,
        value: convertirAMoneda(getEnganche() || 0, includeCents),
        show: showMontoSubcuentaInfonavit,
        classNames: {
          leftIndentation: true,
          boldValue: true,
          boldLabel: true,
        },
      },
      {
        id: 'plazo',
        label: 'Plazo',
        value: plazoValue,
        show: showPlazo,
        classNames: {
          topMargin: showMontoSubcuentaInfonavit,
        },
      },
      {
        id: 'gastos-escritura',
        label: `Escrituración (${gastosEscrituraPercentage}%)`,
        value: convertirAMoneda(getMontoGastosEscritura() || 0, includeCents),
        show: showGastosEscritura,
        // topMargin: 1,
      },
    ];
  }, [
    includeCents,
    showPropietyValue,
    showImporte,
    showEnganche,
    showGastosEscritura,
    showPlazo,
    gastosEscrituraPercentage,
    showMontoSubcuentaInfonavit,
  ]);

  const visibleConditions = React.useMemo(
    () => conditions.filter((condition) => condition.show),
    [conditions],
  );

  return (
    <div className={classes.container}>
      {title && <Typography className={classes.title}>{title}</Typography>}
      <div className={classes.textsContainer}>
        {visibleConditions.map((condition) => (
          <React.Fragment key={condition.id}>
            <Typography
              className={clsx(
                classes.conditionTitle,
                condition.classNames?.leftIndentation && classes.leftIndentation,
                condition.classNames?.boldLabel && classes.boldText,
                condition.classNames?.topMargin && classes.topMargin,
              )}
            >
              {condition.label}
            </Typography>
            <Typography className={
              clsx(
                classes.subtitle,
                condition.classNames?.topMargin && classes.topMargin,
                condition.classNames?.boldValue && classes.boldText,
              )
              }
            >
              {condition.value}
            </Typography>
          </React.Fragment>
        ))}
      </div>

      {children}
    </div>
  );
});

ShowConditionsContainer.propTypes = {
  title: PropTypes.string,
  showPropietyValue: PropTypes.bool,
  showEnganche: PropTypes.bool,
  showImporte: PropTypes.bool,
  showPlazo: PropTypes.bool,
  showGastosEscritura: PropTypes.bool,
  children: PropTypes.node,
  includeCents: PropTypes.bool,
};

ShowConditionsContainer.defaultProps = {
  title: '',
  showPropietyValue: false,
  showEnganche: false,
  showImporte: false,
  showPlazo: false,
  showGastosEscritura: false,
  children: null,
  includeCents: false,
};
